import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Wallet, Gift, Copy, Sparkles, Users, ArrowDownToLine, Share2, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function BullFamily() {
  const { user, authChecked, setAuthModalOpen, refreshUser, formatPrice } = useApp();
  const [summary, setSummary] = useState(null);
  const [redeemPts, setRedeemPts] = useState("");
  const [dobInput, setDobInput] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/wallet/summary").then(({ data }) => setSummary(data)).catch(() => {});

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (!user) {
    if (!authChecked) {
      return <div className="max-w-xl mx-auto px-4 py-24 text-center text-slate-400">Loading your Bull Family account...</div>;
    }
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <Sparkles size={56} className="mx-auto text-[#D4AF37] mb-4" />
        <h1 className="font-display text-2xl font-bold mb-3">Join the Bull Family</h1>
        <p className="text-slate-500 mb-6">Login to earn points, refer friends and use your wallet.</p>
        <Button onClick={() => setAuthModalOpen(true)} className="bg-[#E63946] hover:bg-[#d12836]">Login</Button>
      </div>
    );
  }

  const s = summary || { points: 0, wallet_balance: 0, referral_code: user.referral_code, referrals_count: 0, transactions: [], min_redeem: 100, referrer_bonus: 200, welcome_bonus: 100 };
  const referralLink = `${window.location.origin}/?ref=${s.referral_code}`;

  const copy = (text, msg) => {
    navigator.clipboard?.writeText(text);
    toast.success(msg);
  };

  const share = async () => {
    const text = `Join me on Bull Creations & get ${s.welcome_bonus} bonus points! Use my code ${s.referral_code}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Bull Creations", text, url: referralLink }); } catch {}
    } else {
      copy(referralLink, "Referral link copied!");
    }
  };

  const redeem = async () => {
    const pts = parseInt(redeemPts, 10);
    if (!pts || pts < s.min_redeem) {
      toast.error(`Enter at least ${s.min_redeem} points`);
      return;
    }
    setLoading(true);
    try {
      await api.post("/wallet/redeem", { points: pts });
      toast.success(`Redeemed ${pts} points to your wallet!`);
      setRedeemPts("");
      await refreshUser();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const saveDob = async () => {
    if (!dobInput) return;
    try {
      await api.put("/wallet/profile", { dob: dobInput });
      toast.success("Birthday saved — watch for your bonus! 🎂");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const KIND_LABEL = {
    earn_order: "Points earned", referral: "Referral bonus", welcome: "Welcome bonus",
    redeem: "Redeemed to wallet", checkout_use: "Wallet used at checkout", birthday: "Birthday bonus",
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-2">
        <Sparkles className="text-[#D4AF37]" />
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Bull Family</h1>
      </div>
      <p className="text-slate-500 mb-8">Your rewards club — earn points on every order, refer friends, and spend your wallet credit.</p>

      {/* Membership tier */}
      {s.tier && (
        <div className="rounded-2xl border bg-white dark:bg-slate-900 p-6 mb-6" data-testid="tier-banner">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-display font-black text-white ${s.tier.name === "Gold" ? "bg-gradient-to-br from-[#D4AF37] to-[#b8860b]" : s.tier.name === "Silver" ? "bg-gradient-to-br from-slate-400 to-slate-500" : "bg-gradient-to-br from-amber-700 to-amber-900"}`}>
                {s.tier.name[0]}
              </div>
              <div>
                <div className="font-display font-bold text-lg">{s.tier.name} Member</div>
                <div className="text-xs text-slate-500">Earning {Math.round((s.earn_rate || 0.05) * 100)}% back as points</div>
              </div>
            </div>
            <div className="text-right text-sm">
              <div className="text-slate-400 text-xs">Lifetime spend</div>
              <div className="font-mono-price font-bold">{formatPrice(s.total_spent || 0)}</div>
            </div>
          </div>
          {s.tier.next_name ? (
            <div className="mt-4">
              {(() => {
                const span = s.tier.next_min - s.tier.min;
                const prog = Math.min(100, Math.round(((s.total_spent - s.tier.min) / span) * 100));
                const left = Math.max(0, s.tier.next_min - s.total_spent);
                return (
                  <>
                    <div className="h-2.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#E63946] to-[#D4AF37] transition-all" style={{ width: `${prog}%` }} />
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Spend <strong className="text-[#E63946] font-mono-price">{formatPrice(left)}</strong> more to reach <strong>{s.tier.next_name}</strong> & earn more points.
                    </p>
                  </>
                );
              })()}
            </div>
          ) : (
            <p className="text-xs text-[#D4AF37] font-semibold mt-3">🏆 You're at the top tier — maximum rewards unlocked!</p>
          )}
        </div>
      )}

      {/* balances */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-[#1D3557] to-[#2a4a73] text-white" data-testid="wallet-balance-card">
          <Wallet className="mb-3 text-[#D4AF37]" />
          <div className="text-xs uppercase tracking-wider text-white/70">Wallet Balance</div>
          <div className="font-mono-price font-extrabold text-3xl mt-1">{formatPrice(s.wallet_balance)}</div>
          <div className="text-[11px] text-white/60 mt-1">Use at checkout</div>
        </div>
        <div className="rounded-2xl p-6 bg-gradient-to-br from-[#E63946] to-[#f0625f] text-white" data-testid="points-card">
          <Gift className="mb-3 text-white" />
          <div className="text-xs uppercase tracking-wider text-white/80">Reward Points</div>
          <div className="font-mono-price font-extrabold text-3xl mt-1">{s.points}</div>
          <div className="text-[11px] text-white/70 mt-1">= {formatPrice(s.points)} value</div>
        </div>
        <div className="rounded-2xl p-6 bg-gradient-to-br from-[#D4AF37] to-[#e8c860] text-[#1D3557]" data-testid="referrals-card">
          <Users className="mb-3" />
          <div className="text-xs uppercase tracking-wider opacity-80">Friends Referred</div>
          <div className="font-mono-price font-extrabold text-3xl mt-1">{s.referrals_count}</div>
          <div className="text-[11px] opacity-70 mt-1">+{s.referrer_bonus} pts each</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* referral */}
        <div className="rounded-2xl border bg-white dark:bg-slate-900 p-6">
          <h3 className="font-display font-bold mb-1 flex items-center gap-2"><Share2 size={18} /> Refer & Earn</h3>
          <p className="text-sm text-slate-500 mb-4">
            Share your code — your friend gets <strong>{s.welcome_bonus} points</strong>, and you get <strong>{s.referrer_bonus} points</strong> when they join.
          </p>
          <div className="flex items-center gap-2 p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-dashed">
            <span className="font-mono-price font-extrabold text-xl tracking-widest text-[#E63946] flex-1" data-testid="referral-code">{s.referral_code}</span>
            <Button size="sm" variant="outline" onClick={() => copy(s.referral_code, "Code copied!")} data-testid="copy-code-button">
              <Copy size={15} />
            </Button>
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={() => copy(referralLink, "Referral link copied!")} variant="outline" className="flex-1" data-testid="copy-link-button">
              <Copy size={15} className="mr-1" /> Copy Link
            </Button>
            <Button onClick={share} className="flex-1 bg-[#1D3557] hover:bg-[#162943]" data-testid="share-button">
              <Share2 size={15} className="mr-1" /> Share
            </Button>
          </div>
        </div>

        {/* redeem */}
        <div className="rounded-2xl border bg-white dark:bg-slate-900 p-6">
          <h3 className="font-display font-bold mb-1 flex items-center gap-2"><ArrowDownToLine size={18} /> Redeem Points</h3>
          <p className="text-sm text-slate-500 mb-4">
            Convert points to wallet credit. 1 point = ₹1. Minimum {s.min_redeem} points.
          </p>
          <div className="flex gap-2">
            <Input
              type="number"
              value={redeemPts}
              onChange={(e) => setRedeemPts(e.target.value)}
              placeholder={`e.g. ${s.min_redeem}`}
              data-testid="redeem-points-input"
            />
            <Button onClick={redeem} disabled={loading} className="bg-[#E63946] hover:bg-[#d12836]" data-testid="redeem-button">
              {loading ? "..." : "Redeem"}
            </Button>
          </div>
          <div className="flex gap-2 mt-3">
            {[s.min_redeem, 250, 500].map((p) => (
              <button key={p} onClick={() => setRedeemPts(String(p))} disabled={s.points < p}
                className="text-xs font-semibold px-3 py-1.5 rounded-full border hover:border-[#E63946] disabled:opacity-40">
                {p} pts
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3 flex items-center gap-1">
            <TrendingUp size={13} /> Earn {Math.round((s.earn_rate || 0.05) * 100)}% back as points on every order.
          </p>
        </div>
      </div>

      {/* birthday */}
      <div className="rounded-2xl border bg-white dark:bg-slate-900 p-6 mt-6" data-testid="birthday-card">
        <h3 className="font-display font-bold mb-1 flex items-center gap-2">🎂 Birthday Bonus</h3>
        <p className="text-sm text-slate-500 mb-4">
          Add your birthday and we'll gift you <strong>{s.birthday_bonus || 250} points</strong> every year on your special day.
        </p>
        {s.dob ? (
          <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
            ✓ Birthday saved ({s.dob.slice(-5)}). Your bonus is on the way each year!
          </div>
        ) : (
          <div className="flex gap-2 max-w-sm">
            <Input type="date" value={dobInput} onChange={(e) => setDobInput(e.target.value)} data-testid="dob-input" />
            <Button onClick={saveDob} className="bg-[#1D3557] hover:bg-[#162943]" data-testid="save-dob-button">Save</Button>
          </div>
        )}
      </div>

      {/* history */}
      <div className="rounded-2xl border bg-white dark:bg-slate-900 p-6 mt-6">
        <h3 className="font-display font-bold mb-4">Activity</h3>
        {s.transactions.length === 0 ? (
          <p className="text-slate-400 text-sm py-6 text-center">No activity yet. Start shopping to earn points!</p>
        ) : (
          <div className="space-y-2" data-testid="wallet-activity">
            {s.transactions.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
                <div>
                  <div className="font-medium">{KIND_LABEL[t.kind] || t.kind}</div>
                  <div className="text-xs text-slate-400">{t.description} • {new Date(t.created_at).toLocaleDateString()}</div>
                </div>
                <div className="text-right">
                  {t.points !== 0 && (
                    <span className={`font-mono-price font-bold ${t.points > 0 ? "text-emerald-600" : "text-slate-500"}`}>
                      {t.points > 0 ? "+" : ""}{t.points} pts
                    </span>
                  )}
                  {t.amount !== 0 && (
                    <div className={`font-mono-price text-xs ${t.amount > 0 ? "text-emerald-600" : "text-[#E63946]"}`}>
                      {t.amount > 0 ? "+" : ""}₹{Math.abs(t.amount)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
